import {
  PERSONA_TEMPERAMENT_AXES,
  renderPersona,
  type ArtistPersona,
  type PersonaInfluence,
  type PersonaTemperamentAxis,
} from "../../domain/index.js";
import { Badge } from "../../ui/Badge.js";

// pt-BR display labels for the closed persona vocabulary. Kept in sync with
// `PersonaEditor`; the vocabulary is owned by `@studio/domain`.
const INFLUENCE_LABELS: Record<PersonaInfluence, string> = {
  the_beatles: "the beatles",
  led_zeppelin: "led zeppelin",
  miles_davis: "miles davis",
  daft_punk: "daft punk",
  nina_simone: "nina simone",
  radiohead: "radiohead",
  james_brown: "james brown",
  johnny_cash: "johnny cash",
  bjork: "björk",
  kraftwerk: "kraftwerk",
  bob_marley: "bob marley",
  aphex_twin: "aphex twin",
};

const AXIS_LABELS: Record<PersonaTemperamentAxis, string> = {
  discipline: "disciplina",
  boldness: "ousadia",
  experimental: "experimentação",
};

const AXIS_STEP_LABELS: Record<PersonaTemperamentAxis, Record<number, string>> = {
  discipline: {
    1: "improvisado",
    2: "pouco ensaiado",
    3: "neutro",
    4: "bem ensaiado",
    5: "meticuloso",
  },
  boldness: {
    1: "muito contido",
    2: "reservado",
    3: "neutro",
    4: "confiante",
    5: "expansivo",
  },
  experimental: {
    1: "convencional",
    2: "quase convencional",
    3: "neutro",
    4: "exploratório",
    5: "vanguardista",
  },
};

function influenceLabel(term: string): string {
  return INFLUENCE_LABELS[term as PersonaInfluence] ?? term;
}

function isEmpty(p: ArtistPersona): boolean {
  const temperament = p.temperament ?? {};
  return (
    (p.influences ?? []).length === 0 &&
    PERSONA_TEMPERAMENT_AXES.every((axis) => temperament[axis] === undefined)
  );
}

interface Props {
  persona: ArtistPersona;
  /** Optional message shown when the persona is empty. */
  emptyHint?: string;
}

/**
 * Read-only render of an `ArtistPersona` — influence chips + the three
 * temperament scalars + the rendered prompt snippet. Used by the band's
 * "Persona da banda" card on `BandDetail`. Mirrors `SonicTraitsCard`.
 *
 * The persona shown here is DERIVED from the slot artists (the band stores no
 * persona of its own); this card never exposes an editor.
 */
export function PersonaCard({ persona, emptyHint }: Props): JSX.Element {
  if (isEmpty(persona)) {
    return (
      <p className="font-mono text-xs italic leading-relaxed text-ink-dim">
        {emptyHint ?? "Nenhuma persona declarada — a banda não tem temperamento nem influências."}
      </p>
    );
  }

  const snippet = renderPersona(persona);
  const temperament = persona.temperament ?? {};
  const declaredAxes = PERSONA_TEMPERAMENT_AXES.filter((axis) => temperament[axis] !== undefined);

  return (
    <div className="flex flex-col gap-3">
      <dl className="flex flex-col gap-2">
        {(persona.influences ?? []).length > 0 && (
          <div className="grid grid-cols-[72px_1fr] items-start gap-3">
            <dt className="pt-1 font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
              influências
            </dt>
            <dd className="flex flex-wrap gap-1.5">
              {(persona.influences ?? []).map((term) => (
                <Badge key={term} tone="active" className="lowercase">
                  {influenceLabel(term)}
                </Badge>
              ))}
            </dd>
          </div>
        )}

        {declaredAxes.length > 0 && (
          <div className="grid grid-cols-[72px_1fr] items-start gap-3">
            <dt className="pt-1 font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
              temperamento
            </dt>
            <dd className="flex flex-col gap-1.5">
              {declaredAxes.map((axis) => (
                <TemperamentMeter
                  key={axis}
                  label={AXIS_LABELS[axis]}
                  value={temperament[axis] as number}
                  step={AXIS_STEP_LABELS[axis][temperament[axis] as number] ?? ""}
                />
              ))}
            </dd>
          </div>
        )}
      </dl>

      {snippet && (
        <p className="border-l-2 border-oscillo bg-asphalt/60 px-3 py-2 font-mono text-[11px] leading-relaxed text-ink-muted">
          {snippet}
        </p>
      )}
    </div>
  );
}

/** A read-only 1..5 meter — five segments, the active value lit in butter. */
function TemperamentMeter({
  label,
  value,
  step,
}: {
  label: string;
  value: number;
  step: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-24 font-mono text-[9px] uppercase tracking-widest2 text-ink-muted">
        {label}
      </span>
      <span className="flex gap-0.5" aria-hidden>
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            className={`h-2.5 w-2.5 border ${
              n <= value ? "border-butter bg-butter/70" : "border-border bg-transparent"
            }`}
          />
        ))}
      </span>
      <span className="font-mono text-[9px] uppercase tracking-widest2 tabular-nums text-butter">
        {value} · {step}
      </span>
    </div>
  );
}
